/**
 * MembersTable - Reusable table component for displaying members (Project or Team)
 */
import { ProjectMember, TeamMember } from '@/types';
import {
  Users,
  Crown,
  Shield,
  Eye,
  Trash2,
  Mail,
  Calendar,
  CheckCircle2,
} from 'lucide-react';

type Member = ProjectMember | TeamMember;

interface MembersTableProps {
  members: Member[];
  isAdmin?: boolean;
  onUpdateRole?: (memberId: string, newRole: 'admin' | 'member' | 'viewer') => void;
  onRemoveMember?: (memberId: string, memberEmail: string) => void;
  showActions?: boolean;
  showJoinedDate?: boolean;
  showRemoveAction?: boolean;
  memberShipTypeDetails?:string;
}

const getRoleIcon = (role: string) => {
  switch (role) {
    case 'admin':
      return <Crown className="w-4 h-4 text-yellow-600" />;
    case 'member':
      return <Shield className="w-4 h-4 text-blue-600" />;
    case 'viewer':
      return <Eye className="w-4 h-4 text-gray-600" />;
    default:
      return <Users className="w-4 h-4 text-gray-600" />;
  }
};

const getRoleBadgeColor = (role: string) => {
  switch (role) {
    case 'admin':
      return 'bg-yellow-100 text-yellow-800';
    case 'member':
      return 'bg-blue-100 text-blue-800';
    case 'viewer':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Helper to get member display data (handles both ProjectMember and TeamMember)
const getMemberDisplayData = (member: Member) => {
  // Check if it's a ProjectMember
  if ('user_display_name' in member || 'username' in member) {
    const pm = member as ProjectMember;
    return {
      displayName: pm.user_display_name || pm.username || 'User',
      email: pm.user_email || '',
      avatarUrl: pm.user_avatar_url,
      initial: (pm.user_display_name || pm.username || 'U').charAt(0).toUpperCase(),
    };
  }

  // Otherwise it's a TeamMember
  const tm = member as TeamMember;
  const displayName =
    tm.user_name ||
    tm.user?.display_name ||
    tm.user?.username ||
    tm.user_email ||
    tm.user?.email ||
    'User';
  const email = tm.user_email || tm.user?.email || '';
  const initialSource =
    tm.user_name ||
    tm.user_email ||
    tm.user?.display_name ||
    tm.user?.email ||
    'U';
  const initial = initialSource.charAt(0).toUpperCase();

  return {
    displayName,
    email,
    avatarUrl: tm.user_avatar || tm.user?.avatar_url,
    initial,
  };
};

export const MembersTable = ({
  members,
  isAdmin = false,
  onUpdateRole,
  onRemoveMember,
  showActions = true,
  showJoinedDate = true,
  showRemoveAction = true,
  memberShipTypeDetails="direct"
}: MembersTableProps) => {
  const handleRemoveClick = (member: Member) => {
    if (!onRemoveMember) return;
    const displayData = getMemberDisplayData(member);
    onRemoveMember(member.user_id, displayData.email || 'this user');
  };

  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50 sticky top-0 z-10">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Member
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Role
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Type
          </th>
          {showJoinedDate && (
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Joined
            </th>
          )}
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Status
          </th>
          {showActions && isAdmin && (
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          )}
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {members.map((member) => {
          const displayData = getMemberDisplayData(member);
          const membershipType = (member as any).membership_type as string | undefined;
          const isTeamMember = membershipType === 'team';

          return (
            <tr key={member.id} className="hover:bg-gray-50">
              {/* Member */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    {displayData.avatarUrl ? (
                      <img
                        className="h-10 w-10 rounded-full"
                        src={displayData.avatarUrl}
                        alt=""
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium">
                        {displayData.initial}
                      </div>
                    )}
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {displayData.displayName}
                    </div>
                    {displayData.email && (
                      <div className="text-sm text-gray-500 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {displayData.email}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                  {getRoleIcon(member.role)}
                  {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    isTeamMember
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-orange-100 text-orange-800'
                  }`}
                >
                  {isTeamMember ? (
                    <Users className="w-3 h-3" />
                  ) : (
                    <Shield className="w-3 h-3" />
                  )}
                  {isTeamMember ? 'Team' : 'Direct'}
                </span>
              </td>
              {showJoinedDate && (
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(member.joined_at).toLocaleDateString()}
                  </div>
                </td>
              )}
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${member.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                  {member.is_active && <CheckCircle2 className="w-3 h-3" />}
                  {member.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              {showActions && isAdmin && (
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    {onUpdateRole && (
                      <select
                        value={member.role}
                        onChange={(e) => onUpdateRole(member.id
                          , e.target.value as 'admin' | 'member' | 'viewer')}
                        className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    )}
                    {showRemoveAction && onRemoveMember && membershipType===memberShipTypeDetails&&(
                      <button
                        onClick={() => handleRemoveClick(member)}
                        className="text-red-600 hover:text-red-900"
                        title="Remove member"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

